import React, {useCallback, useEffect, useState} from 'react'
import {alpha, Box, useTheme} from '@mui/material'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import {useAppDispatch, useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {setUnreadNotificationsCount} from 'src/slices/userSlice'
import {buildColumns, CommunicationRow} from './communicationsColumns'
import CommunicationDetailDialogNew from './CommunicationDetailDialogNew'

// Same filterConfig as the legacy page (src/pages/communications/Communications.tsx) —
// hardcoded, not PSP-driven.
const FILTER_CONFIG: FilterItem[] = [
  {id: 'date', label: 'Data', type: 'rangePicker'},
]

/**
 * New-style equivalent of src/pages/communications/Communications.tsx. No page title is
 * rendered here — HeaderBar derives it from the "communications" nav item label — and there
 * is no "create" action, matching the legacy behavior (only the "Filtra" panel and the
 * read-only detail dialog opened on row click).
 */
export default function CommunicationsListNew(): JSX.Element {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const columnOptions = useAppSelector(
    state => state.pspConfigReducer.data.communicationsConfigurations?.columnOptions
  )

  const [rows, setRows] = useState<CommunicationRow[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})
  const [selected, setSelected] = useState<CommunicationRow | undefined>()
  const [detailOpen, setDetailOpen] = useState(false)

  const fetchData = useCallback(async () => {
    // `date` comes from the Filters' `rangePicker` type as a [fromISO, toISO] pair
    // (see FilterResult shape in Filters.tsx) — same mapping the legacy fetchNotifications does.
    const dateFilter = activeFilters.find(f => f.property === 'date' && f.type === 'rangePicker')
    const filterParams = {
      startDate: dateFilter?.value?.[0],
      endDate: dateFilter?.value?.[1],
    }

    const data = await apiClient.getNotifications(
      {
        ...filterParams,
        size: pagination.rowsPerPage,
        page: pagination.page,
        sort: pagination.order || undefined,
      },
      setStatus
    )
    if (Array.isArray(data)) setRows(data)

    // `getNotificationsCount` has no `setStatus` param (see apiClient/index.ts) — it performs
    // the request directly and throws on failure, so it needs its own guard here, unlike
    // `getNotifications` above.
    const count = await apiClient.getNotificationsCount(filterParams).catch(() => undefined)
    if (count !== undefined) setTotalElements(Number(count) || 0)

    const unreadCount = await apiClient.getNotificationsCount({hasBeenRead: false}).catch(() => undefined)
    if (unreadCount !== undefined) dispatch(setUnreadNotificationsCount(Number(unreadCount) || 0))
  }, [activeFilters, pagination, dispatch])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleRowClick = async (row: CommunicationRow) => {
    setSelected(row)
    setDetailOpen(true)
    if (!row.hasBeenRead) {
      const [notificationId, messageIndex] = row.id.split('-')
      await apiClient.patchNotificationMessage(notificationId, messageIndex, {hasBeenRead: true})
      fetchData()
    }
  }

  const columns = buildColumns(columnOptions)

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={FILTER_CONFIG} onSubmit={handleFiltersSubmit} />
      </Box>

      {status === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatus(STATUS.FULFILLED)} sx={{mb: 2}}>
          {'Errore nel recuperare le notifiche.'}
        </InlineMessage>
      )}

      <DataTable<CommunicationRow>
        columns={columns}
        rows={rows}
        keyField="id"
        loading={status === STATUS.PENDING}
        emptyMessage="Nessuna notifica trovata"
        onRowClick={handleRowClick}
        // Subtle brand-tinted background so an unread row stands out from the rest of the
        // list at a glance, alongside the type-icon color already used for the same signal.
        getRowSx={(row) => (!row.hasBeenRead ? {backgroundColor: alpha(theme.palette.primary.main, 0.05)} : undefined)}
        mobileCardTitle={(row) => row.title ?? '—'}
        totalElements={totalElements}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
      />

      <CommunicationDetailDialogNew
        communication={selected}
        onClose={() => setDetailOpen(false)}
        open={detailOpen}
      />
    </Box>
  )
}
